import { useState, useEffect } from 'react';
import { Star, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewSection = ({ productId, productName }) => {
  const { isAuthenticated, token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [guestName, setGuestName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  // Fetch reviews on mount
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(`${API}/reviews/${productId}`);
        setReviews(res.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    if (productId) fetchReviews();
  }, [productId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      toast.error('Te rugam sa scrii o recenzie');
      return;
    }
    
    // For guest users, name is required
    if (!isAuthenticated && !guestName.trim()) {
      toast.error('Te rugam sa introduci numele tau');
      return;
    }

    setSubmitting(true);
    try {
      const reviewData = {
        product_id: productId,
        rating,
        comment,
        guest_name: !isAuthenticated ? guestName.trim() : undefined
      };
      
      const headers = isAuthenticated ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.post(`${API}/reviews`, reviewData, { headers });
      
      setReviews([res.data, ...reviews]);
      setComment('');
      setGuestName('');
      setRating(5);
      setShowForm(false);
      toast.success('Recenzie trimisa cu succes!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Eroare la trimiterea recenziei';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const StarRating = ({ value, interactive = false, size = 'w-5 h-5' }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          onClick={interactive ? () => setRating(star) : undefined}
          onMouseEnter={interactive ? () => setHoverRating(star) : undefined}
          onMouseLeave={interactive ? () => setHoverRating(0) : undefined}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
          disabled={!interactive}
        >
          <Star 
            className={`${size} ${
              star <= (interactive ? (hoverRating || rating) : value)
                ? 'fill-[#c00000] text-[#c00000]'
                : 'fill-transparent text-white/20'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );

  return (
    <div className="border-t border-white/5 pt-12 mt-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
        <div>
          <h2 className="font-heading font-bold text-2xl tracking-wider text-white mb-2">
            RECENZII CLIENTI
          </h2>
          {reviews.length > 0 ? (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <StarRating value={Math.round(parseFloat(averageRating))} />
                <span className="font-heading font-bold text-xl text-white">
                  {averageRating}
                </span>
              </div>
              <span className="font-body text-sm text-white/50">
                ({reviews.length} {reviews.length === 1 ? 'recenzie' : 'recenzii'})
              </span>
            </div>
          ) : (
            <p className="font-body text-sm text-white/50">
              Fii primul care lasa o recenzie pentru acest produs
            </p>
          )}
        </div>

        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#c00000] text-white hover:bg-[#a00000] rounded-none px-6 py-3 font-heading tracking-wider"
          data-testid="write-review-btn"
        >
          SCRIE O RECENZIE
        </Button>
      </div>

      {/* Review Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-[#121212] border border-white/5 p-6 mb-8"
        >
          <form onSubmit={handleSubmit}>
            {/* Guest Name Field */}
            {!isAuthenticated && (
              <div className="mb-4">
                <label className="font-body text-sm text-white/70 block mb-2">Numele Tau *</label>
                <Input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Introdu numele tau"
                  className="bg-[#0a0a0a] border-white/10 text-white rounded-none"
                  data-testid="review-name-input"
                />
              </div>
            )}
            
            <div className="mb-4">
              <label className="font-body text-sm text-white/70 block mb-2">Nota Ta *</label>
              <StarRating value={rating} interactive size="w-8 h-8" />
            </div>
            
            <div className="mb-4">
              <label className="font-body text-sm text-white/70 block mb-2">Recenzia Ta *</label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Impartaseste experienta ta cu acest produs..."
                className="bg-[#0a0a0a] border-white/10 text-white rounded-none min-h-[120px]"
                data-testid="review-textarea"
              />
            </div>
            
            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#c00000] text-white hover:bg-[#a00000] rounded-none px-8"
                data-testid="submit-review-btn"
              >
                {submitting ? 'SE TRIMITE...' : 'TRIMITE RECENZIA'}
              </Button>
              <Button
                type="button"
                onClick={() => setShowForm(false)}
                variant="outline"
                className="bg-transparent border-white/10 text-white rounded-none px-8"
              >
                ANULEAZA
              </Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-white/20 border-t-[#c00000] rounded-full animate-spin mx-auto" />
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-6">
          {reviews.map((review, idx) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-[#121212] border border-white/5 p-6"
              data-testid={`review-${idx}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white/30" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white">{review.user_name}</h4>
                    <p className="font-body text-xs text-white/40 mt-1">
                      {new Date(review.created_at).toLocaleDateString('ro-RO', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <StarRating value={review.rating} size="w-4 h-4" />
              </div>
              <p className="font-body text-white/70 leading-relaxed">{review.comment}</p>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-[#121212] border border-white/5">
          <Star className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <h3 className="font-heading text-lg text-white mb-2">Nicio recenzie inca</h3>
          <p className="font-body text-sm text-white/50">
            Fii primul care impartaseste experienta cu acest produs
          </p>
        </div>
      )}
    </div>
  );
};

export default ReviewSection;
